import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { History, ChevronDown } from "lucide-react";
import { useApi } from "../hooks/useApi";
import Button from "./Buttons";

/**
 * "History" beside View Risks on an Enterprise org / deal / BU card: the scope's previous assessment
 * rounds, each opening that round's risk page read-only. Lists only superseded rounds — while a
 * reassessment is still being answered or analysed, the previous round is live and View Risks opens
 * it, so it is not history yet. Read-only is decided by the server (the round's supersededAt), never
 * by how the page was reached.
 */
export default function EnterpriseAssessmentHistoryMenu({ assessmentId, hasHistory }) {
  const apiCall = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rounds, setRounds] = useState(null);   // null = not loaded yet
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  useEffect(() => {
    if (!open || rounds !== null) return;
    let cancelled = false;
    setFailed(false);
    // roundsOnly: dates, statuses and risk counts only, not the chart's per-round scores.
    apiCall("GET", `/enterpriseAssessments/${assessmentId}/history?roundsOnly=true`)
      .then((r) => {
        if (cancelled) return;
        const entries = Array.isArray(r?.entries) ? r.entries : [];
        setRounds(entries.filter((e) => e.supersededAt));
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [open, rounds, apiCall, assessmentId]);

  if (!hasHistory || !assessmentId) return null;

  return (
    <div className="relative" ref={ref} onClick={(e) => e.stopPropagation()}>
      <Button size="sm" variant="secondary" className="px-1 py-1 h-9 text-xs"
        aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <History className="h-4 w-4 mr-1" />
        History
        <ChevronDown className="h-3 w-3 ml-1" />
      </Button>
      {open && (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-72 rounded-md border border-gray-200 bg-white shadow-lg py-1"
          data-testid="enterprise-history-menu">
          <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-gray-400">Previous assessments (read-only)</div>
          {failed && <div className="px-3 py-2 text-xs text-gray-500">The previous assessments could not be loaded.</div>}
          {!failed && rounds === null && <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>}
          {!failed && rounds?.length === 0 && <div className="px-3 py-2 text-xs text-gray-500">No previous assessments.</div>}
          {rounds?.map((r) => (
            <button key={r.assessmentId} type="button" role="menuitem"
              className="block w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => navigate(`/EnterpriseAssessments/${r.assessmentId}/risks`)}>
              Assessment #{r.assessmentId} · {r.date ? new Date(r.date).toLocaleDateString() : "—"} · {r.riskCount ?? 0} risk{r.riskCount === 1 ? "" : "s"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The one-line note a card shows while its reassessment is unfinished. */
export function ReassessmentNote({ status }) {
  if (!status) return null;
  const failed = status === "analysis_failed";
  return (
    <div className={`text-[11px] mt-1 ${failed ? "text-red-600" : "text-amber-700"}`} data-testid="reassessment-note">
      {failed
        ? "Reassessment failed — continue it to retry. The previous assessment's risks stay active."
        : "Reassessment in progress — the previous assessment's risks stay active until it completes."}
    </div>
  );
}
